import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';

interface BillingSummary {
  planLabel: string;
  subscriptionStatus: string;
  billingLocked: boolean;
  hasStripeCustomer: boolean;
  trialExpired: boolean;
}

export function BillingLockedPage() {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<BillingSummary>('/billing/summary')
      .then((res) => {
        if (!cancelled) setSummary(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err) || 'Failed to load billing state');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await api.post<{ url: string }>('/billing/portal-session', {});
      window.location.href = res.data.url;
    } catch (err) {
      setError(getApiErrorMessage(err) || 'Could not open billing portal');
      setOpeningPortal(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <div className="max-w-lg w-full border border-red-200 bg-red-50 rounded-lg p-6 space-y-4 text-center">
        <h1 className="text-xl font-bold text-red-900">Your subscription is inactive</h1>
        <p className="text-sm text-red-800">
          {summary?.trialExpired
            ? 'Your trial has ended. Reactivate billing to keep using Akoreps.'
            : 'Akoreps needs an active subscription to continue. Reactivate billing to restore access.'}
        </p>
        {summary && (
          <div className="text-xs text-red-700">
            Plan: <strong>{summary.planLabel}</strong> · Status:{' '}
            <strong className="capitalize">{summary.subscriptionStatus}</strong>
          </div>
        )}
        {error && (
          <div className="text-xs text-red-900 bg-red-100 border border-red-300 rounded p-2">
            {error}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          {summary?.hasStripeCustomer ? (
            <button
              type="button"
              onClick={handleOpenPortal}
              disabled={openingPortal}
              className="px-4 py-2 text-sm font-semibold text-white bg-red-700 rounded hover:bg-red-800 disabled:opacity-60"
            >
              Open billing portal
            </button>
          ) : null}
          <Link
            to="/billing"
            className="px-4 py-2 text-sm font-semibold text-red-900 bg-white border border-red-300 rounded hover:bg-red-100"
          >
            Choose a plan
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BillingLockedPage;
